import { Checkbox, message, TableColumnsType } from 'antd';
import {
  AntModal,
  Button,
  DefaultFactorColumn,
  Footer,
  Loading,
  QuickEdit,
  TitleBar,
} from '../../../../../components';
import TableV3 from '../../../../../components/generic/Table/TableV3';
import { tableSortingMethods } from '../../../../../utils';
import { useNavigate } from 'react-router';
import { useState } from 'react';
import StandaloneForm from '../../../../../components/generic/Form/StandaloneForm';

import { ApolloError, useMutation, useQuery } from '@apollo/client';
import QueryErrorBoundary from '../../../../../components/error-handling';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { debounce } from 'lodash';
import { QualitativeFactor } from '../../../../dataInputs/data-inputs/qualitative-factors/types';
import {
  CREATE_QUALITATIVE_FACTOR,
  DELETE_QUALITATIVE_FACTOR,
} from '../../../../dataInputs/data-inputs/qualitative-factors/queries';
import { formFields } from './formFields';
import {
  GET_QUALITATIVE_FACTORS_DASHBOARD,
  UPDATE_QUALITATIVE_FACTOR,
} from './queries';
import TitleUpdater from '../../../../../components/common/TitleUpdater';
import { StatusDisplay } from '../../../components/StatusDisplay';
import { sortQualitativeFactors } from '../../../../../utils/qualitativeFactorUtils';

type QualitativeFactorDashboard = {
  uuid: string;
  name: string;
  applicationsTagged: number;
  lastUpdated: string;
  supportedByITApplication: boolean;
  isDefault: boolean;
  isDirty: boolean;
};

type qualitativeFactorsDashboard = {
  getQualitativeFactorsDashboard: QualitativeFactorDashboard[];
};

const QualitativeFactors = () => {
  const REFETCH_QUERY = 'GetQualitativeFactorsDashboard';
  const [isAddQualitativeFactorModalOpen, setIsAddQualitativeFactorModalOpen] =
    useState(false);
  const [checkedQualitativeFactors, setCheckedQualitativeFactors] = useState<{
    [key: string]: boolean;
  }>({});
  const [createQualitativeFactorLoading, setCreateQualitativeFactorLoading] =
    useState(false);
  const [currentQualitativeFactor, setCurrentQualitativeFactor] =
    useState<QualitativeFactor | null>(null);

  const navigate = useNavigate();

  const { data, loading, error } = useQuery<qualitativeFactorsDashboard>(
    GET_QUALITATIVE_FACTORS_DASHBOARD,
    {
      onCompleted: (data) => {
        const initialState: { [key: string]: boolean } = {};
        data?.getQualitativeFactorsDashboard.forEach((qualitativeFactor) => {
          initialState[qualitativeFactor.uuid] =
            qualitativeFactor.supportedByITApplication;
        });
        setCheckedQualitativeFactors(initialState);
      },
    },
  );

  const [updateSupportAppDependency, { loading: updateCheckboxLoading }] =
    useMutation(UPDATE_QUALITATIVE_FACTOR, {
      refetchQueries: [REFETCH_QUERY],
    });

  const [createQualitativeFactor] = useMutation(CREATE_QUALITATIVE_FACTOR, {
    refetchQueries: [REFETCH_QUERY],
  });

  const [updateQualitativeFactor] = useMutation(UPDATE_QUALITATIVE_FACTOR, {
    refetchQueries: [REFETCH_QUERY],
  });

  const [deleteQualitativeFactor] = useMutation(DELETE_QUALITATIVE_FACTOR, {
    refetchQueries: [REFETCH_QUERY],
  });

  const qualitativeFactorsData = data?.getQualitativeFactorsDashboard ?? [];

  const orderedQualitativeFactorsData = sortQualitativeFactors(
    qualitativeFactorsData,
  );

  const loadingStates = loading || updateCheckboxLoading;

  const handleCheckboxToggle = async (e: CheckboxChangeEvent, uuid: string) => {
    const currentValue = e.target.checked;

    try {
      const response = await updateSupportAppDependency({
        variables: {
          data: {
            uuid,
            supportedByITApplication: currentValue,
          },
        },
      });

      if (response.data) {
        message.success('Changes Saved');
        setCheckedQualitativeFactors((prev) => ({
          ...prev,
          [uuid]: currentValue,
        }));
      }
    } catch (e) {
      if (e instanceof ApolloError) {
        message.error(e.message);
      }
    }
  };

  const debouncedHandleCheckbox = debounce(
    (e: CheckboxChangeEvent, uuid: string) => {
      handleCheckboxToggle(e, uuid);
    },
    300,
  );

  const columns: TableColumnsType<QualitativeFactorDashboard> = [
    {
      title: 'Qualitative Factor',
      dataIndex: 'name',
      key: 'name',
      width: 180,
      fixed: 'left',
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: (text: string, record: QualitativeFactorDashboard) => (
        <DefaultFactorColumn text={text} isDefault={record?.isDefault} />
      ),
    },
    {
      width: 120,
      dataIndex: 'lastUpdated',
      key: 'lastUpdated',
      align: 'left',
      sorter: (a, b) => tableSortingMethods.date(a.lastUpdated, b.lastUpdated),
      render: (dateString: string, record: QualitativeFactorDashboard) => (
        <StatusDisplay isDirty={record.isDirty} lastUpdated={dateString} />
      ),
    },
    {
      title: '# of Applications Tagged',
      width: 70,
      dataIndex: 'applicationsTagged',
      key: 'applicationsTagged',
      align: 'center',
      sorter: (a, b) =>
        tableSortingMethods.number(a.applicationsTagged, b.applicationsTagged),
      render: (applicationsTagged: number) => (
        <span className="custom-table--column">{applicationsTagged ?? 0}</span>
      ),
    },
    {
      title: 'No Application Dependency',
      width: 70,
      dataIndex: 'applicationDependency',
      key: 'applicationDependency',
      align: 'center',
      sorter: (a, b) =>
        tableSortingMethods.boolean(
          a.supportedByITApplication,
          b.supportedByITApplication,
        ),
      render: (_, record) => {
        const { uuid, applicationsTagged } = record;

        const isChecked =
          checkedQualitativeFactors[uuid] && !applicationsTagged;

        return (
          <span className="custom-table--column">
            <Checkbox
              onChange={(checkedValue) => {
                if (checkedValue && applicationsTagged > 0) {
                  message.error('Factor has IT applications mapped');
                  return;
                }
                debouncedHandleCheckbox(checkedValue, uuid);
              }}
              checked={isChecked}
              defaultChecked={isChecked}
            />
          </span>
        );
      },
    },
  ];

  const handleAddQualitativeFactor = () => {
    setCurrentQualitativeFactor(null);
    setIsAddQualitativeFactorModalOpen(true);
  };

  const handleToggleQualitativeFactorEdit = (record: QualitativeFactor) => {
    setCurrentQualitativeFactor(record);
    setIsAddQualitativeFactorModalOpen(true);
  };

  const extendOptions = (
    <Button
      type="tertiary"
      className="font-normal min-w-[280px]"
      onClick={() =>
        navigate(`/cyber/enterprise-level/qualitative-factors/bulk`)
      }
    >
      Bulk Tagging
    </Button>
  );

  const extendActions = (record: QualitativeFactor) => {
    return (
      <div className="relative w-full h-7">
        <div className="absolute -left-11">
          {!record.isDefault && (
            <Button
              type="edit"
              onClick={() => handleToggleQualitativeFactorEdit(record)}
            >
              <QuickEdit />
            </Button>
          )}
        </div>

        {!checkedQualitativeFactors[record.uuid] ? (
          <Button
            type="primary-rounded"
            className="w-full text-sm font-semibold min-w-[90px]"
            onClick={() =>
              navigate(
                `/cyber/enterprise-level/qualitative-factors/${record.uuid}/it-application-selection`,
              )
            }
          >
            Tag & Describe Applications
          </Button>
        ) : null}
      </div>
    );
  };

  let modalContent;

  if (createQualitativeFactorLoading) {
    modalContent = <Loading />;
  } else {
    modalContent = (
      <StandaloneForm
        fields={formFields}
        createGQL={createQualitativeFactor}
        updateGQL={updateQualitativeFactor}
        deleteGQL={deleteQualitativeFactor}
        currentRecord={currentQualitativeFactor}
        onFinalize={() => setIsAddQualitativeFactorModalOpen(false)}
        setIsLoading={setCreateQualitativeFactorLoading}
      />
    );
  }

  return (
    <div>
      <TitleUpdater title={`Enterprise Qualitative Factors Tagging`} />
      <TitleBar
        title="Enterprise Qualitative Factors Tagging"
        subtitle={`All Qualitative Factors`}
      />
      <QueryErrorBoundary error={error}>
        <TableV3
          columns={columns}
          data={orderedQualitativeFactorsData}
          loading={loadingStates}
          useBorderedSection
          editable
          editableOptions={{
            update: false,
          }}
          extendActions={extendActions}
          extendOptions={extendOptions}
        />
      </QueryErrorBoundary>

      <Button
        type="primary"
        className="w-full text-sm font-bold max-w-[260px] mx-auto mt-7 py-2"
        onClick={handleAddQualitativeFactor}
      >
        Add Factor
      </Button>
      <Footer backPath={'/cyber/enterprise-level'} />
      <AntModal
        title={`${
          currentQualitativeFactor ? 'Edit' : 'Add'
        } Qualitative Factor`}
        openModal={isAddQualitativeFactorModalOpen}
        onCancel={() => setIsAddQualitativeFactorModalOpen(false)}
      >
        {modalContent}
      </AntModal>
    </div>
  );
};

export default QualitativeFactors;
